import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Navbar,
  Nav,
  Modal,
  Spinner,
  Alert,
  OverlayTrigger,
  Tooltip,
  Form,
  Pagination,
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import {
  User,
  authService,
  friendshipService,
  achievementService,
  AchievementCatalogEntry,
  loadUnreadNotificationCount,
} from '../services/api';
import LanguageSelector from './LanguageSelector';
import NavbarBrandLogo from './NavbarBrandLogo';
import UserNavbarMenus from './UserNavbarMenus';
import NotificationBell from './NotificationBell';
import NavbarMusicToggle from './NavbarMusicToggle';
import PwaInstallButton from './PwaInstallButton';
import LoginPage from './LoginPage';
import FriendsList from './FriendsList';
import PendingRequests from './PendingRequests';
import { achievementCircleColor } from '../utils/achievementRarity';
import { formatAchievementRarity } from '../utils/formatAchievementRarity';
import { achievementIconSrcFromKey } from '../utils/achievementIconAsset';
import { useMatchMedia } from '../hooks/useMatchMedia';
import { CRITERIA_TOTAL_CORRECT_ANSWERS_BY_TAG } from '../utils/achievementCriteriaTypes';
import './HomePage.css';
import './QuizList.css';
import './LeaderboardPage.css';
import './ProfileAchievementsSection.css';
import './AchievementsCatalogPage.css';

const PAGE_SIZE_DESKTOP = 12;
const PAGE_SIZE_MOBILE = 10;

type CatalogUnlockFilter = 'all' | 'obtained' | 'not_obtained';

const AchievementsCatalogPage: React.FC = () => {
  const { t, ready, i18n } = useSafeTranslation();
  const navigate = useNavigate();

  /** Bootstrap lg — desktop grid vs mobile (2 cols per row). */
  const isDesktopCatalog = useMatchMedia('(min-width: 992px)');
  const catalogPageSize = isDesktopCatalog ? PAGE_SIZE_DESKTOP : PAGE_SIZE_MOBILE;

  const [user, setUser] = useState<User | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [showPendingRequests, setShowPendingRequests] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const [items, setItems] = useState<AchievementCatalogEntry[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [page, setPage] = useState(1);
  /** Default `asc` = Common → Rare → Epic (grandeza); `desc` = Epic → Rare → Common */
  const [rarityOrder, setRarityOrder] = useState<'asc' | 'desc'>('asc');
  const [unlockFilter, setUnlockFilter] = useState<CatalogUnlockFilter>('all');
  const [themeFilterInput, setThemeFilterInput] = useState('');
  const [themeSearch, setThemeSearch] = useState('');
  const [pagination, setPagination] = useState({
    totalCount: 0,
    page: 1,
    pageSize: PAGE_SIZE_MOBILE,
    totalPages: 0,
  });

  const [mineAchievementIds, setMineAchievementIds] = useState<Set<string>>(new Set());
  /** Mini modal: TOTAL_CORRECT_ANSWERS_BY_TAG criteria count hint */
  const [tagCriteriaModalCount, setTagCriteriaModalCount] = useState<number | null>(null);

  const loadUser = useCallback(async () => {
    setIsLoadingUser(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUser(null);
        setUnreadNotificationCount(0);
        return;
      }
      const u = await authService.getProfile();
      setUser(u);
      setUnreadNotificationCount(await loadUnreadNotificationCount(u));
    } catch {
      localStorage.removeItem('authToken');
      setUser(null);
      setUnreadNotificationCount(0);
    } finally {
      setIsLoadingUser(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  useEffect(() => {
    if (!user) {
      setMineAchievementIds(new Set());
      setUnlockFilter('all');
      return;
    }
    let cancelled = false;
    void achievementService
      .getMine()
      .then((mine) => {
        if (cancelled) return;
        setMineAchievementIds(new Set(mine.map((m) => m.achievementId)));
      })
      .catch(() => {
        if (!cancelled) setMineAchievementIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (user) {
      void friendshipService.getPendingRequests().then((r) => setPendingRequestsCount(r.length)).catch(() => {});
    }
  }, [user]);

  /** Reset página antes dos effects que carregam dados (evita GET com page/size inconsistentes ao redimensionar). */
  useLayoutEffect(() => {
    setPage(1);
  }, [catalogPageSize]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setThemeSearch(themeFilterInput.trim());
    }, 400);
    return () => window.clearTimeout(id);
  }, [themeFilterInput]);

  useEffect(() => {
    setPage(1);
  }, [themeSearch]);

  const loadCatalog = useCallback(async () => {
    setListLoading(true);
    setLoadError(false);
    try {
      const data = await achievementService.getCatalog(
        page,
        catalogPageSize,
        rarityOrder,
        unlockFilter,
        themeSearch
      );
      setItems(data.items);
      setPagination({
        totalCount: data.totalCount,
        page: data.page,
        pageSize: data.pageSize,
        totalPages: data.totalPages,
      });
    } catch {
      setLoadError(true);
      setItems([]);
    } finally {
      setListLoading(false);
    }
  }, [page, rarityOrder, unlockFilter, catalogPageSize, themeSearch]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    const onLang = () => {
      void loadCatalog();
    };
    i18n.on('languageChanged', onLang);
    return () => {
      i18n.off('languageChanged', onLang);
    };
  }, [i18n, loadCatalog]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
    setUnreadNotificationCount(0);
    window.location.href = '/';
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRarityOrderChange = (next: 'asc' | 'desc') => {
    setRarityOrder(next);
    setPage(1);
  };

  const handleUnlockFilterChange = (next: CatalogUnlockFilter) => {
    setUnlockFilter(next);
    setPage(1);
  };

  if (isLoadingUser) {
    return (
      <div className="home-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page leaderboard-page achievements-catalog-page">
      <Navbar bg="white" expand="lg" className="home-navbar" collapseOnSelect>
        <Container fluid>
          <Navbar.Brand
            as={Link}
            to="/"
            className="navbar-brand d-flex align-items-center"
            aria-label="Quizter Club"
          >
            <NavbarBrandLogo />
            <span className="d-none d-lg-inline">Quizter Club</span>
          </Navbar.Brand>

          <div className="d-flex d-lg-none align-items-center ms-auto gap-1 gap-sm-2 navbar-mobile-toolbar">
            <PwaInstallButton className="flex-shrink-0" iconOnly />
            <LanguageSelector user={user} isInline={true} />
            <NavbarMusicToggle className="flex-shrink-0" />
            {user && (
              <NotificationBell
                className="d-lg-none"
                unreadCount={unreadNotificationCount}
                onUnreadCountChange={setUnreadNotificationCount}
              />
            )}
            {user ? (
              <Navbar.Toggle aria-controls="basic-navbar-nav" />
            ) : (
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                className="d-inline-flex align-items-center justify-content-center flex-shrink-0 home-navbar-login-icon"
                onClick={() => setShowLoginModal(true)}
                title={t('common.login', 'Login')}
                aria-label={t('common.login', 'Login')}
              >
                <i className="bi bi-box-arrow-in-right" aria-hidden />
              </Button>
            )}
          </div>
          <Navbar.Collapse id="basic-navbar-nav" className="justify-content-lg-end">
            <Nav className="flex-column flex-lg-row align-items-stretch align-items-lg-center w-100 justify-content-lg-end">
              <div className="d-none d-lg-flex align-items-center gap-2 me-lg-3">
                <PwaInstallButton />
                <LanguageSelector user={user} isInline={true} />
                {!user && <NavbarMusicToggle />}
              </div>
              {user ? (
                <UserNavbarMenus
                  user={user}
                  pendingRequestsCount={pendingRequestsCount}
                  unreadNotificationCount={unreadNotificationCount}
                  onUnreadNotificationCountChange={setUnreadNotificationCount}
                  onOpenFriends={() => setShowFriendsList(true)}
                  onOpenPending={() => setShowPendingRequests(true)}
                  onLogout={handleLogout}
                />
              ) : (
                <Nav.Item className="d-none d-lg-block">
                  <Button variant="outline-primary" onClick={() => setShowLoginModal(true)}>
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    {t('common.login', 'Login')}
                  </Button>
                </Nav.Item>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container fluid className="home-content leaderboard-page-shell">
        <Row className="justify-content-center">
          <Col xs={12} lg={10} xl={9}>
            <div className="quiz-list-page leaderboard-page-content w-100">
              <Row className="mb-4">
                <Col>
                  <div className="quiz-list-header">
                    <h1>{t('achievements.catalogPageTitle', 'Achievements')}</h1>
                    <p className="text-muted mb-0">
                      {t(
                        'achievements.catalogSubtitle',
                        'All achievements you can unlock in Quizter Club.'
                      )}
                    </p>
                  </div>
                </Col>
              </Row>

              <Row className="mb-3 g-2 g-lg-3 align-items-end achievements-catalog-filters-row">
                <Col xs={12} md={4} lg={4} className="min-w-0">
                  <Form.Group controlId="achievements-catalog-theme-filter" className="mb-0">
                    <Form.Label className="small text-muted mb-1 d-block">
                      {t('achievements.catalogThemeFilterLabel', 'Search by theme')}
                    </Form.Label>
                    <div className="position-relative achievements-catalog-theme-field">
                      <Form.Control
                        type="text"
                        inputMode="search"
                        className={themeFilterInput ? 'pe-5' : undefined}
                        value={themeFilterInput}
                        onChange={(e) => setThemeFilterInput(e.target.value)}
                        placeholder={t(
                          'achievements.catalogThemeFilterPlaceholder',
                          'Search by theme...'
                        )}
                        autoComplete="off"
                        spellCheck={false}
                        aria-label={t('achievements.catalogThemeFilterLabel', 'Search by theme')}
                      />
                      {themeFilterInput ? (
                        <Button
                          type="button"
                          variant="link"
                          className="achievements-catalog-theme-clear position-absolute top-50 end-0 translate-middle-y py-0 px-2 border-0 text-secondary shadow-none"
                          title={t('achievements.catalogThemeFilterClear', 'Clear theme filter')}
                          aria-label={t('achievements.catalogThemeFilterClear', 'Clear theme filter')}
                          onClick={() => setThemeFilterInput('')}
                        >
                          <i className="bi bi-x-lg" aria-hidden />
                        </Button>
                      ) : null}
                    </div>
                  </Form.Group>
                </Col>
                <Col xs={12} md={4} lg={4} className="min-w-0">
                  <Form.Group controlId="achievements-catalog-rarity-sort" className="achievements-catalog-sort mb-0">
                    <Form.Label className="small text-muted mb-1 d-block">
                      {t('achievements.catalogSortLabel', 'Sort by rarity')}
                    </Form.Label>
                    <Form.Select
                      value={rarityOrder}
                      onChange={(e) => handleRarityOrderChange(e.target.value as 'asc' | 'desc')}
                      aria-label={t('achievements.catalogSortLabel', 'Sort by rarity')}
                    >
                      <option value="asc">
                        {t('achievements.catalogSortLeastRareFirst', 'Least rare first')}
                      </option>
                      <option value="desc">
                        {t('achievements.catalogSortMostRareFirst', 'Most rare first')}
                      </option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={4} lg={4} className="min-w-0">
                  <Form.Group controlId="achievements-catalog-unlock-filter" className="achievements-catalog-sort mb-0">
                    <Form.Label className="small text-muted mb-1 d-block">
                      {t('achievements.catalogUnlockFilterLabel', 'Filter by status')}
                    </Form.Label>
                    <Form.Select
                      value={unlockFilter}
                      onChange={(e) =>
                        handleUnlockFilterChange(e.target.value as CatalogUnlockFilter)
                      }
                      disabled={!user}
                      aria-label={t('achievements.catalogUnlockFilterLabel', 'Filter by status')}
                      title={
                        !user
                          ? t(
                              'achievements.catalogUnlockFilterRequiresLogin',
                              'Sign in to filter by obtained / not obtained.'
                            )
                          : undefined
                      }
                    >
                      <option value="all">{t('achievements.catalogUnlockFilterAll', 'All')}</option>
                      <option value="obtained">
                        {t('achievements.catalogUnlockFilterObtained', 'Obtained')}
                      </option>
                      <option value="not_obtained">
                        {t('achievements.catalogUnlockFilterNotObtained', 'Not obtained')}
                      </option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {loadError && (
                <Alert variant="danger" className="d-flex flex-wrap align-items-center gap-2">
                  <span>{t('achievements.catalogLoadError', 'Could not load achievements.')}</span>
                  <Button variant="outline-danger" size="sm" onClick={() => void loadCatalog()}>
                    {t('achievements.retryLoad', 'Try again')}
                  </Button>
                </Alert>
              )}

              {!loadError && pagination.totalCount > 0 && (
                <Row className="mb-3">
                  <Col>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <p className="text-muted mb-0 flex-grow-1 min-w-0">
                        {t('quiz.showing', 'Showing')}{' '}
                        {(pagination.page - 1) * pagination.pageSize + 1}{' '}
                        {t('quiz.to', 'to')}{' '}
                        {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)}{' '}
                        {t('quiz.of', 'of')} {pagination.totalCount}{' '}
                        {t('achievements.catalogResultsEntity', 'achievements')}
                      </p>
                      <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-md-auto">
                        <p className="text-muted mb-0 text-nowrap">
                          {t('quiz.page', 'Page')} {pagination.page} {t('quiz.of', 'of')}{' '}
                          {pagination.totalPages}
                        </p>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              {listLoading && (
                <div className="text-center py-5" role="status" aria-live="polite">
                  <Spinner animation="border" />
                  <span className="visually-hidden d-block mt-2">
                    {t('achievements.loadingFullPage', 'Loading achievement list…')}
                  </span>
                </div>
              )}

              {!listLoading && !loadError && items.length === 0 && (
                <Card body className="text-muted text-center">
                  {unlockFilter !== 'all' || themeSearch.length > 0
                    ? t(
                        'achievements.catalogEmptyFiltered',
                        'No achievements match this filter.'
                      )
                    : t('achievements.catalogEmpty', 'No achievements in the catalog yet.')}
                </Card>
              )}

              {!listLoading && items.length > 0 && (
                <Row className="g-3 achievements-catalog-grid">
                  {items.map((a) => {
                    const bg = achievementCircleColor(a.rarity);
                    const rarityLabel = formatAchievementRarity(a.rarity, t);
                    const iconSrc = achievementIconSrcFromKey(a.iconAssetKey);
                    const circleAria = ready
                      ? i18n.t('achievements.ariaBadge', {
                          name: a.name,
                          rarity: rarityLabel,
                          points: a.points,
                        })
                      : `${a.name}, ${rarityLabel}, ${a.points} points`;
                    const themeLine = a.theme?.trim() ?? '';
                    const tooltip = (
                      <Tooltip id={`cat-${a.achievementId}`}>
                        <div className="achievement-tooltip-content small">
                          {themeLine ? (
                            <div className="achievement-tooltip-theme achievements-catalog-tooltip-theme">
                              {themeLine}
                            </div>
                          ) : null}
                          <div className="achievement-tooltip-title">{a.name}</div>
                          {a.description?.trim() ? (
                            <div className="achievement-tooltip-desc">{a.description.trim()}</div>
                          ) : null}
                          <div className="achievement-tooltip-meta">
                            {rarityLabel} · {t('achievements.pointsLabel', 'Points')}: {a.points}
                            {a.isSecret ? (
                              <>
                                {' '}
                                · {t('achievements.catalogSecretLabel', 'Secret')}
                              </>
                            ) : null}
                          </div>
                        </div>
                      </Tooltip>
                    );
                    const isObtained = user && mineAchievementIds.has(a.achievementId);
                    const showTagCriteriaInfo =
                      !a.isSecret &&
                      a.criteriaType === CRITERIA_TOTAL_CORRECT_ANSWERS_BY_TAG &&
                      (a.criteriaValue ?? 0) > 0;
                    return (
                      <Col key={a.achievementId} xs={6} sm={6} md={4} lg={4}>
                        <Card className="achievements-catalog-card h-100 border-0 shadow-sm position-relative">
                          {showTagCriteriaInfo ? (
                            <Button
                              type="button"
                              variant="light"
                              size="sm"
                              className="achievements-catalog-tag-criteria-info-btn rounded-circle border shadow-sm"
                              aria-label={t(
                                'achievements.catalogTagCriteriaInfoAria',
                                'How to unlock this achievement'
                              )}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setTagCriteriaModalCount(a.criteriaValue!);
                              }}
                            >
                              <i className="bi bi-info-circle" aria-hidden />
                            </Button>
                          ) : null}
                          {isObtained ? (
                            <span
                              className="achievements-catalog-obtained-badge"
                              title={t(
                                'achievements.catalogObtainedBadgeTitle',
                                'You have unlocked this achievement'
                              )}
                              aria-label={t(
                                'achievements.catalogObtainedBadgeTitle',
                                'You have unlocked this achievement'
                              )}
                            >
                              {t('achievements.catalogObtainedBadge', 'Obtained')}
                            </span>
                          ) : null}
                          <Card.Body className="d-flex flex-column align-items-center text-center py-4">
                            <OverlayTrigger placement="top" trigger={['hover', 'focus']} overlay={tooltip}>
                              <div
                                className={`d-flex flex-shrink-0 ${themeLine ? 'mb-2' : 'mb-3'}`}
                                tabIndex={0}
                              >
                                <div
                                  className="profile-achievement-circle d-flex align-items-center justify-content-center achievements-catalog-circle"
                                  style={{ backgroundColor: bg }}
                                  aria-label={circleAria}
                                  role="img"
                                >
                                  {a.isSecret ? (
                                    <i className="bi bi-lock-fill profile-achievement-fallback-icon" aria-hidden />
                                  ) : iconSrc ? (
                                    <img src={iconSrc} alt="" className="profile-achievement-circle-img" />
                                  ) : (
                                    <i className="bi bi-trophy-fill profile-achievement-fallback-icon" aria-hidden />
                                  )}
                                </div>
                              </div>
                            </OverlayTrigger>
                            {themeLine ? (
                              <div
                                className="achievements-catalog-theme text-truncate w-100 px-1"
                                title={themeLine}
                              >
                                {themeLine}
                              </div>
                            ) : null}
                            <div className="fw-semibold small text-truncate w-100 px-1" title={a.name}>
                              {a.name}
                            </div>
                            <div className="small text-muted mt-1">
                              {rarityLabel} · {a.points} {t('achievements.pointsShort', 'pts')}
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )}

              {!listLoading && pagination.totalPages > 1 && (
                <Row className="mt-4">
                  <Col className="d-flex justify-content-center">
                    <Pagination>
                      <Pagination.First
                        disabled={pagination.page <= 1}
                        onClick={() => handlePageChange(1)}
                      />
                      <Pagination.Prev
                        disabled={pagination.page <= 1}
                        onClick={() => handlePageChange(pagination.page - 1)}
                      />
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        const pageNum =
                          Math.max(1, Math.min(pagination.totalPages - 4, pagination.page - 2)) + i;
                        return (
                          <Pagination.Item
                            key={pageNum}
                            active={pageNum === pagination.page}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </Pagination.Item>
                        );
                      })}
                      <Pagination.Next
                        disabled={pagination.page >= pagination.totalPages}
                        onClick={() => handlePageChange(pagination.page + 1)}
                      />
                      <Pagination.Last
                        disabled={pagination.page >= pagination.totalPages}
                        onClick={() => handlePageChange(pagination.totalPages)}
                      />
                    </Pagination>
                  </Col>
                </Row>
              )}

              <div className="back-to-home-card d-flex justify-content-center">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  type="button"
                  onClick={() => navigate('/')}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  {t('navigation.backToHome', 'Back to Home')}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <Modal
        show={tagCriteriaModalCount !== null}
        onHide={() => setTagCriteriaModalCount(null)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h6 mb-0">
            {t('achievements.catalogTagCriteriaInfoTitle', 'How to unlock')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {tagCriteriaModalCount !== null
            ? i18n.t('achievements.catalogTagCriteriaInfoBody', {
                count: tagCriteriaModalCount,
                defaultValue:
                  'To unlock this achievement you must answer the tagged question correctly {{count}} times.',
              })
            : null}
        </Modal.Body>
      </Modal>

      <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('auth.loginRequired', 'Login Required')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LoginPage
            onLoginSuccess={() => {
              setShowLoginModal(false);
              void loadUser();
            }}
          />
        </Modal.Body>
      </Modal>

      {showFriendsList && <FriendsList onClose={() => setShowFriendsList(false)} />}
      {showPendingRequests && (
        <PendingRequests
          onClose={() => setShowPendingRequests(false)}
          onRequestResponded={() => {
            void friendshipService.getPendingRequests().then((r) => setPendingRequestsCount(r.length));
          }}
        />
      )}
    </div>
  );
};

export default AchievementsCatalogPage;
